/* ──────────────────────────────────────────────────────────────
 *  useSessionStore — session-level analytics tracking
 *
 *  Mirrors the Python session JSON (session_id, counters, events).
 *  Sends snapshots to the backend periodically and on unload.
 * ────────────────────────────────────────────────────────────── */

import { create } from 'zustand';
import type {
  SessionEventType,
  SessionEvent,
  SessionEventCounters,
} from '@/types/session';

interface SessionState {
  sessionId: string;
  sessionStartTime: string;
  counters: SessionEventCounters;
  events: SessionEvent[];

  /* ── Actions ──────────────────────────────────────────── */
  /** Record a trackable event (increments counter + appends to log). */
  trackEvent: (type: SessionEventType, payload?: Record<string, unknown>) => void;
  /** Replace all counters (e.g. after restoring from persistence). */
  setCounters: (c: SessionEventCounters) => void;
  /** Duration in seconds since session started. */
  getDuration: () => number;
  /** Reset session (new UUID, clear counters). */
  resetSession: () => void;
}

function generateId(): string {
  return crypto.randomUUID();
}

const ZERO_COUNTERS: SessionEventCounters = {
  compute: 0,
  pdf: 0,
  ppt: 0,
  synera: 0,
  marc: 0,
  stp: 0,
  exp: 0,
  reset: 0,
  rating: 0,
};

export const useSessionStore = create<SessionState>()((set, get) => ({
  sessionId: generateId(),
  sessionStartTime: new Date().toISOString(),
  counters: { ...ZERO_COUNTERS },
  events: [],

  trackEvent: (type, payload) =>
    set((s) => {
      const event: SessionEvent = {
        type,
        timestamp: new Date().toISOString(),
        ...(payload ? { payload } : {}),
      };
      return {
        counters: {
          ...s.counters,
          [type]: (s.counters[type] ?? 0) + 1,
        },
        events: [...s.events, event],
      };
    }),

  setCounters: (c) => set({ counters: c }),

  getDuration: () => {
    const start = new Date(get().sessionStartTime).getTime();
    return Math.round((Date.now() - start) / 1000);
  },

  resetSession: () =>
    set({
      sessionId: generateId(),
      sessionStartTime: new Date().toISOString(),
      counters: { ...ZERO_COUNTERS },
      events: [],
    }),
}));
